import React, { useState, useEffect } from "react";
import Tabs from "src/components/Shared/Tabs/Tabs";
import { classNames } from "src/helpers/classNames";
import { apiRequest } from "src/async/apiUtils";
import Preloader from "src/components/Shared/LoadingAnimations/Preloader";
import {
  Squares2X2Icon,
  SparklesIcon,
  KeyIcon,
  CommandLineIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import {
  getCustomAppStatusBadgeStyles,
  getCustomAppTypeBadgeStyles,
  getAppInitials,
  getAppGradient,
} from "src/helpers/customAppHelper";
import { CustomAppOverview } from "./CustomAppOverview";
import { CustomAppAnalysis } from "./CustomAppAnalysis";
import { CustomAppEnvVars } from "./CustomAppEnvVars";
import { CustomAppLogs } from "./CustomAppLogs";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import Button from "src/components/Shared/Buttons/Button";

const CustomAppBodyContent = ({ data = null }) => {
  const [appDetails, setAppDetails] = useState(data);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentTab, setCurrentTab] = useState(null);

  const fetchFullDetails = async (id) => {
    if (!id) return;
    try {
      setLoadingDetails(true);
      const res = await apiRequest("GET", `/custom-apps/:id`, {
        params: { id },
      });
      if (res?.data?.status === 200 && res?.data?.data) {
        setAppDetails(res.data.data);
      }
    } catch (err) {
      // Fallback to basic data
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (data?._id) {
      setAppDetails(data);
      fetchFullDetails(data._id);
    }
  }, [data?._id]);

  const { className: statusClass, label: statusLabel, dotClass, glowClass } =
    getCustomAppStatusBadgeStyles(appDetails?.status);
  const { className: typeClass, label: typeLabel } =
    getCustomAppTypeBadgeStyles(appDetails?.appType);

  const initials = getAppInitials(appDetails?.name);
  const gradient = getAppGradient(appDetails?.name);

  const domainDisplay = appDetails?.publicUrl
    ? appDetails.publicUrl.replace(/^https?:\/\//, "")
    : appDetails?.hostname;

  const tabs = [
    {
      name: "Overview",
      icon: Squares2X2Icon,
      component: <CustomAppOverview data={appDetails} />,
    },
    {
      name: "Analysis & Build",
      icon: SparklesIcon,
      component: <CustomAppAnalysis data={appDetails} />,
    },
    {
      name: "Environment Variables",
      icon: KeyIcon,
      component: <CustomAppEnvVars data={appDetails} />,
    },
    {
      name: "Logs & Console",
      icon: CommandLineIcon,
      component: <CustomAppLogs data={appDetails} />,
    },
  ];

  return (
    <div className="flex flex-col min-h-full">
      {/* Premium Cloud Console Hero Header */}
      <div className="relative border-b border-gray-200/80 bg-gradient-to-b from-slate-50/80 via-white to-white px-8 pb-6 pt-7">
        <div className="flex flex-col gap-y-4">
          {/* Top Row: Breadcrumb & Quick Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-medium text-gray-400">
              <span className="text-gray-500">Custom Applications</span>
              <span>/</span>
              <span className="text-indigo-600 font-semibold truncate max-w-xs">
                {appDetails?.name || "Application"}
              </span>
            </div>

            {/* Action Buttons */}
            {appDetails?.publicUrl && (
              <div className="flex items-center gap-2">
                <CopyToClipboard
                  text={appDetails.publicUrl}
                  onCopy={() => toast.success("Public URL copied to clipboard")}
                >
                  <Button
                    type="button"
                    version="default"
                    className="flex !h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 hover:border-gray-300 transition-all"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                    <span>Copy URL</span>
                  </Button>
                </CopyToClipboard>

                <a
                  href={appDetails.publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-4 text-xs font-semibold text-white shadow-sm hover:from-indigo-500 hover:to-purple-500 hover:shadow transition-all"
                >
                  <span>Open App</span>
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </div>

          {/* Main Title Row with Avatar */}
          <div className="flex items-start gap-4">
            <div
              className={classNames(
                "flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-base font-bold shadow-md ring-1 ring-black/5",
                gradient
              )}
            >
              {appDetails?.appType === "frontend" ? (
                <GlobeAltIcon className="h-7 w-7 text-white" />
              ) : (
                <span className="tracking-wider">{initials}</span>
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 truncate">
                  {appDetails?.name || "Untitled Application"}
                </h1>

                {/* Status Indicator Pill */}
                <div
                  className={classNames(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold",
                    statusClass
                  )}
                >
                  <span
                    className={classNames(
                      "h-2 w-2 rounded-full",
                      dotClass,
                      glowClass
                    )}
                  />
                  <span>{statusLabel}</span>
                </div>

                {/* App Type Pill */}
                {appDetails?.appType && (
                  <div
                    className={classNames(
                      "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-medium uppercase tracking-wider",
                      typeClass
                    )}
                  >
                    {typeLabel}
                  </div>
                )}
              </div>

              {/* URL & Subtitle row */}
              <div className="flex flex-col gap-y-1">
                <div className="flex items-center gap-2.5 flex-wrap text-xs text-gray-500">
                  {domainDisplay && (
                    <span className="inline-flex items-center gap-1 font-mono text-gray-600">
                      <GlobeAltIcon className="h-3.5 w-3.5 text-gray-400" />
                      <span>{domainDisplay}</span>
                    </span>
                  )}
                  {appDetails?.publicUrl?.startsWith("https") && (
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 text-[11px]">
                      <ShieldCheckIcon className="h-3.5 w-3.5" />
                      <span>SSL Secured</span>
                    </span>
                  )}
                </div>
                {appDetails?.description && (
                  <p className="text-xs text-gray-500 max-w-2xl leading-normal mt-0.5">
                    {appDetails.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Container */}
      <div className="px-8 pb-6 pt-2 flex-1">
        {loadingDetails ? (
          <div className="py-16">
            <Preloader color="text-gray-400 fill-indigo-600" />
          </div>
        ) : (
          <Tabs
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            tabs={tabs}
          />
        )}
      </div>
    </div>
  );
};

export default CustomAppBodyContent;
